import React, { useEffect, useState } from 'react';
import Helpers from '../../Helpers/Helpers';

const BannerCarousel = ({ section = "home_top_offers" }) => {
    const helpers = Helpers();
    const [desktopBanners, setDesktopBanners] = useState([]);
    const [mobileBanners, setMobileBanners] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch desktop banners
    const fetchDesktopBanners = async () => {
        try {
            const response = await helpers.httpRequest(`/banners?section=${section}&device_type=desktop`, 'GET');
            if (response && response.status === 'success' && response.data) {
                const activeBanners = response.data.filter(banner => banner.is_active && banner.full_image);
                activeBanners.sort((a, b) => (a.priority || 999) - (b.priority || 999));
                setDesktopBanners(activeBanners);
            }
        } catch (error) {
            console.error('Error fetching desktop banners:', error);
            setDesktopBanners([]);
        }
    };

    // Fetch mobile banners
    const fetchMobileBanners = async () => {
        try {
            const response = await helpers.httpRequest(`/banners?section=${section}&device_type=mobile`, 'GET');
            if (response && response.status === 'success' && response.data) {
                const activeBanners = response.data.filter(banner => banner.is_active && banner.full_image);
                activeBanners.sort((a, b) => (a.priority || 999) - (b.priority || 999));
                setMobileBanners(activeBanners);
            }
        } catch (error) {
            console.error('Error fetching mobile banners:', error);
            setMobileBanners([]);
        }
    };

    // Initialize component
    useEffect(() => {
        const fetchBanners = async () => {
            setLoading(true);
            await Promise.all([fetchDesktopBanners(), fetchMobileBanners()]);
            setLoading(false);
        };
        fetchBanners();
    }, []);

    // Handle banner click
    const handleBannerClick = (banner) => {
        if (banner.redirect_url && banner.redirect_url.trim()) {
            if (banner.redirect_url.startsWith('http')) {
                window.open(banner.redirect_url, '_blank');
            } else {
                window.location.href = banner.redirect_url;
            }
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading banners...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="banner-carousel-container">
            {/* Desktop Banner Carousel - Show on medium screens and up (≥768px) */}
            <div className="desktop-product-banner-carousel d-none d-md-block">
                {desktopBanners.length > 0 && (
                    <div id="desktopBannerCarousel" className="carousel slide" data-bs-ride="carousel">
                        {/* Indicators - only show if more than 1 banner */}
                        {desktopBanners.length > 1 && (
                            <div className="carousel-indicators">
                                {desktopBanners.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        data-bs-target="#desktopBannerCarousel"
                                        data-bs-slide-to={index}
                                        className={index === 0 ? "active" : ""}
                                        aria-current={index === 0 ? "true" : "false"}
                                        aria-label={`Slide ${index + 1}`}
                                    ></button>
                                ))}
                            </div>
                        )}
                        
                        {/* Carousel Items */}
                        <div className="carousel-inner">
                            {desktopBanners.map((banner, index) => (
                                <div 
                                    key={banner._id || index} 
                                    className={`carousel-item ${index === 0 ? 'active' : ''}`}
                                    style={{ height: '350px' }}
                                >
                                    <img 
                                        src={banner.full_image} 
                                        className="d-block w-100" 
                                        alt={banner.title || `Desktop Banner ${index + 1}`}
                                        style={{ 
                                            cursor: banner.redirect_url ? 'pointer' : 'default',
                                            height: '100%',
                                        }}
                                        onClick={() => handleBannerClick(banner)}
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                    />
                                </div>
                            ))}
                        </div>
                        
                        {/* Controls - only show if more than 1 banner */}
                        {desktopBanners.length > 1 && (
                            <>
                                <button className="carousel-control-prev" type="button" data-bs-target="#desktopBannerCarousel" data-bs-slide="prev">
                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                    <span className="visually-hidden">Previous</span>
                                </button>
                                <button className="carousel-control-next" type="button" data-bs-target="#desktopBannerCarousel" data-bs-slide="next">
                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                    <span className="visually-hidden">Next</span>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile Banner Carousel - Show on small screens only (<768px) */}
            <div className="mobile-product-banner-carousel d-block d-md-none">
                {mobileBanners.length > 0 && (
                    <div id="mobileBannerCarousel" className="carousel slide" data-bs-ride="carousel">
                        {/* Indicators - only show if more than 1 banner */}
                        {mobileBanners.length > 1 && (
                            <div className="carousel-indicators">
                                {mobileBanners.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        data-bs-target="#mobileBannerCarousel"
                                        data-bs-slide-to={index}
                                        className={index === 0 ? "active" : ""}
                                        aria-current={index === 0 ? "true" : "false"}
                                        aria-label={`Slide ${index + 1}`}
                                    ></button>
                                ))}
                            </div>
                        )}
                        
                        {/* Carousel Items */}
                        <div className="carousel-inner">
                            {mobileBanners.map((banner, index) => (
                                <div 
                                    key={banner._id || index} 
                                    className={`carousel-item ${index === 0 ? 'active' : ''}`}
                                    style={{ height: '350px' }}
                                >
                                    <img 
                                        src={banner.full_image} 
                                        className="d-block w-100" 
                                        alt={banner.title || `Mobile Banner ${index + 1}`}
                                        style={{ 
                                            cursor: banner.redirect_url ? 'pointer' : 'default',
                                            height: '100%'
                                        }}
                                        onClick={() => handleBannerClick(banner)}
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                    />
                                </div>
                            ))}
                        </div>
                        
                        {/* Controls - only show if more than 1 banner */}
                        {mobileBanners.length > 1 && (
                            <>
                                <button className="carousel-control-prev" type="button" data-bs-target="#mobileBannerCarousel" data-bs-slide="prev">
                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                    <span className="visually-hidden">Previous</span>
                                </button>
                                <button className="carousel-control-next" type="button" data-bs-target="#mobileBannerCarousel" data-bs-slide="next">
                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                    <span className="visually-hidden">Next</span>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BannerCarousel;